import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, Stack, Typography } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { BlockSkeleton, EmptyState } from '../../../components/ui';
import { Part } from '../../../types';
import { fontFamilyMono, radius } from '../../../theme/tokens';
import { byUrgency, isCriticallyLow } from './helpers';

interface LowStockPartsProps {
  parts: Part[];
  /** All parts at or under their reorder point, of which `parts` is the head. */
  total: number;
  loading: boolean;
}

export const LOW_STOCK_SHOWN = 5;

/** Parts at or under their reorder point, most urgent first. */
const LowStockParts: React.FC<LowStockPartsProps> = ({ parts, total, loading }) => {
  const shown = [...parts].sort(byUrgency).slice(0, LOW_STOCK_SHOWN);

  return (
    <Card sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontSize: '1rem' }}>
            Parts running low
          </Typography>
          <Typography variant="body2" color="text.secondary">
            At or below reorder point
          </Typography>
        </Box>
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${radius.sm}px`,
            bgcolor: 'warning.light',
            color: 'warning.dark',
          }}
        >
          <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
      </Stack>

      {loading && parts.length === 0 ? (
        <BlockSkeleton lines={4} />
      ) : shown.length === 0 ? (
        <EmptyState dense title="Nothing to reorder" description="Every part is above its reorder point." />
      ) : (
        <Box>
          {shown.map((part) => {
            const critical = isCriticallyLow(part);
            const onHand = Number(part.onHand);
            return (
              <Stack
                key={part.id}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ py: 1.5, borderTop: 1, borderColor: 'divider' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {part.name}
                  </Typography>
                  <Typography
                    component="div"
                    noWrap
                    sx={{ fontFamily: fontFamilyMono, fontSize: '0.71875rem', color: 'text.secondary' }}
                  >
                    {part.sku}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 650,
                    whiteSpace: 'nowrap',
                    color: critical ? 'error.dark' : 'warning.dark',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {onHand <= 0 ? 'Out of stock' : `${onHand} left`}
                </Typography>
              </Stack>
            );
          })}
          <Button
            component={RouterLink}
            to="/admin/purchase-orders"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mt: 1 }}
          >
            {total > shown.length ? `Reorder parts (${total} low)` : 'Reorder parts'}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default LowStockParts;
